"use client";

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { getStoredAuth } from "../../../utils/authStorage";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { fetchMyPlans, createMyOrder, fetchMyPayments } from "../../../services/billingService";
import { formatPaise } from "../../../utils/billingFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15;

const computeDisplayAmounts = (baseAmount, gstRate, discountAmount = 0) => {
  const taxableAmount = baseAmount - discountAmount;
  const gstAmount = Math.round((taxableAmount * gstRate) / 100);
  return { taxableAmount, gstAmount, totalAmount: taxableAmount + gstAmount };
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const PlanSelection = ({ hasExistingSubscription, onPaymentConfirmed }) => {
  const { user } = getStoredAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [status, setStatus] = useState(null); // { type: 'confirming'|'timeout'|'failed'|'cancelled'|'mismatch', message }

  useEffect(() => {
    fetchMyPlans()
      .then((res) => setPlans(res.data?.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const pollForPayment = async (razorpayOrderId) => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      await wait(POLL_INTERVAL_MS);
      try {
        const res = await fetchMyPayments();
        const payment = (res.data?.payments || []).find((p) => p.razorpayOrderId === razorpayOrderId);
        if (payment?.status === "SUCCESS") {
          setStatus(null);
          toast.success("Payment confirmed! Your subscription is now active.");
          onPaymentConfirmed?.();
          return;
        }
        if (payment?.status === "AMOUNT_MISMATCH") {
          setStatus({ type: "mismatch", message: "Payment could not be completed automatically. Please contact support." });
          return;
        }
      } catch {
        // transient fetch failure during polling — keep trying until the attempt cap
      }
    }
    setStatus({
      type: "timeout",
      message: "Still confirming your payment — this can take a minute. Check again shortly, or contact support if it persists.",
    });
  };

  const handlePayNow = async (plan) => {
    setStatus(null);
    setPayingPlanId(plan._id);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Could not load the payment gateway. Check your connection and try again.");
        setPayingPlanId(null);
        return;
      }

      const orderRes = await createMyOrder({
        planId: plan._id,
        purpose: hasExistingSubscription ? "RENEWAL" : "NEW_SUBSCRIPTION",
      });
      const order = orderRes.data;

      let handlerFired = false;
      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.razorpayOrderId,
        name: "Clynox",
        description: `${plan.name} — ${plan.durationMonths} months`,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        handler: () => {
          handlerFired = true;
          setStatus({ type: "confirming", message: "Payment received — confirming with our system..." });
          pollForPayment(order.razorpayOrderId);
        },
        modal: {
          ondismiss: () => {
            if (!handlerFired) {
              setStatus({ type: "cancelled", message: "Payment cancelled." });
            }
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setStatus({
          type: "failed",
          message: `Payment failed: ${response.error?.description || "Please try again."}`,
        });
      });
      rzp.open();
    } catch (error) {
      toast.error(extractApiErrorMessage(error));
    } finally {
      setPayingPlanId(null);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-gray-400 text-sm">Loading plans...</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Available Plans</h2>

      {status && (
        <Alert
          className={
            status.type === "failed" || status.type === "mismatch"
              ? "border-red-300 bg-red-50"
              : status.type === "cancelled"
              ? "border-gray-300 bg-gray-50"
              : "border-amber-300 bg-amber-50"
          }
        >
          <AlertDescription
            className={
              status.type === "failed" || status.type === "mismatch"
                ? "text-red-800"
                : status.type === "cancelled"
                ? "text-gray-700"
                : "text-amber-800"
            }
          >
            {status.message}
          </AlertDescription>
        </Alert>
      )}

      {plans.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">No plans available right now — contact support.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const discountAmount = plan.discountAmount || 0;
            const { gstAmount, totalAmount } = computeDisplayAmounts(plan.baseAmount, plan.gstRate, discountAmount);
            const isPaying = payingPlanId === plan._id;
            return (
              <Card key={plan._id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">{plan.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {plan.description && <p className="text-xs text-gray-500">{plan.description}</p>}
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Price</span>
                      <span>{formatPaise(plan.baseAmount)}</span>
                    </div>
                    {discountAmount > 0 && (
                      <div className="flex justify-between text-green-700">
                        <span>Discount</span>
                        <span>− {formatPaise(discountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>GST ({plan.gstRate}%)</span>
                      <span>{formatPaise(gstAmount)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-gray-900 border-t pt-1">
                      <span>Total</span>
                      <span>{formatPaise(totalAmount)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{plan.durationMonths} months · up to {plan.maxUsers} users</p>
                  <Button className="w-full mt-2" disabled={payingPlanId !== null} onClick={() => handlePayNow(plan)}>
                    {isPaying ? "Processing..." : hasExistingSubscription ? "Renew Now" : "Pay Now"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlanSelection;
